// Whether the patient has actually been told about a booking.
//
// The panel's booked box said "It is on the calendar. The confirmation above has
// not been sent." — as a hard-coded string, so it went on saying it after the
// office had sent. That box is the only place tying the two acts together
// (booking the slot, telling the patient) and it tracked one and lied about the
// other. The risk is not confusion: it is the patient being told twice, or
// nobody telling them because each person assumed a colleague had.
//
// "Sent" means a reply left the practice after the booking was made. Not
// particular wording — staff reword constantly and fold confirmations into
// longer replies, so matching on text would show "not sent" for messages that
// plainly were.

export interface ThreadMessage {
  direction?: string;
  createdAt?: string | Date | null;
  isInternalNote?: boolean;
}

const at = (value: string | Date | null | undefined): number => {
  if (!value) return NaN;
  const ms = value instanceof Date ? value.getTime() : Date.parse(String(value));
  return Number.isNaN(ms) ? NaN : ms;
};

// The moment the patient was told, or null if they have not been.
export function confirmationSentAt(
  bookedAt: string | Date | null | undefined,
  messages: ThreadMessage[] | null | undefined,
): Date | null {
  const booked = at(bookedAt);
  if (Number.isNaN(booked)) return null;

  const candidates = (messages || [])
    // Only what actually goes to the patient. An internal note is a note to
    // ourselves, and the booking-link entry is direction 'none' because nobody
    // sent anything.
    .filter((m) => m?.direction === 'outbound' && !m?.isInternalNote)
    .map((m) => at(m?.createdAt))
    .filter((ms) => !Number.isNaN(ms) && ms >= booked)
    .sort((a, b) => a - b);

  return candidates.length ? new Date(candidates[0]) : null;
}

export function confirmationSent(
  bookedAt: string | Date | null | undefined,
  messages: ThreadMessage[] | null | undefined,
): boolean {
  return confirmationSentAt(bookedAt, messages) !== null;
}
